import { RemoveListener, NotifyReactEvents } from 'Phaser/EventBus2';

export class AdaptiveTempoController {
  private base: number;
  private delta: number = 0;
  private isActive: boolean = false;
  private startTimestamp: number = 0;
  private endTimestamp: number = 0;
  private goalTempo: number;
  private isFreePlay: boolean;
  private lowerLimitTempo: number;
  private upperLimitTempo: number;

  private hasMasteredGoalTempo: boolean = false;

  private onDone: (finalTempo: number) => void;
  private callbackScope: object | undefined;
  private isEnabled: boolean;

  constructor(
    base: number,
    onDone: (finalTempo: number) => void,
    callbackScope: object | undefined,
    goalTempo: number,
    isFreePlay: boolean,
    lowerLimitTempo: number,
    upperLimitTempo: number,
    isEnabled: boolean,
  ) {
    console.debug('[ata] AdaptiveTempoController constructor', {
      base,
      goalTempo,
      isFreePlay,
      lowerLimitTempo,
      upperLimitTempo,
      isEnabled,
    });
    this.isEnabled = isEnabled;
    this.base = base;
    this.delta = 0;
    this.onDone = onDone;
    this.callbackScope = callbackScope;
    this.goalTempo = goalTempo;
    this.isFreePlay = isFreePlay;
    this.lowerLimitTempo = lowerLimitTempo;
    this.upperLimitTempo = upperLimitTempo;
  }

  setIsEnabled(isEnabled: boolean) {
    if (!isEnabled) {
      this.abortAdaptiveSegment({ tempoRestorePoint: this.base });
    }
    this.isEnabled = isEnabled;
  }

  setGoalTempo(goalTempo: number) {
    console.debug('[ata] setGoalTempo', goalTempo);
    this.goalTempo = goalTempo;
  }

  setIsFreePlay(isFreePlay: boolean) {
    this.isFreePlay = isFreePlay;
  }

  destroy() {
    RemoveListener('AbortAdaptiveTempo');
    this.hasMasteredGoalTempo = false;
  }

  shouldStartAdaptiveSegment(delta: number, base: number) {
    if (!this.isEnabled) return false;
    console.debug('[ata] shouldStartAdaptiveSegment', {
      base,
      delta,
      lowerLimitTempo: this.lowerLimitTempo,
      upperLimitTempo: this.upperLimitTempo,
      goalTempo: this.goalTempo,
    });
    if (delta < 0) {
      // Slow down resets the goal tempo mastery flag
      this.hasMasteredGoalTempo = false;
      const shouldContinue = base > this.lowerLimitTempo;
      if (!shouldContinue) {
        // We're at the lowest tempo, suggest a level change
        NotifyReactEvents.SuggestLevelChange({
          performance: 'poor',
        });
      }
      return shouldContinue;
    } else {
      const shouldStart = base < Math.min(this.upperLimitTempo, this.goalTempo);
      if (!shouldStart && this.isFreePlay) {
        console.debug(
          'this.goalTempo',
          this.goalTempo,
          'this.upperLimitTempo',
          this.upperLimitTempo,
        );
        // When the user *correctly (>95%)* plays atleast two consecutive phrases at the goal tempo,
        // we notify the React app that the goal tempo has been mastered.
        if (this.hasMasteredGoalTempo)
          NotifyReactEvents.SuggestLevelChange({
            performance: 'good',
            customGoal: this.goalTempo !== this.upperLimitTempo,
          });
        else this.hasMasteredGoalTempo = true;
      }
      return shouldStart;
    }
  }

  startAdaptiveSegment(
    base: number,
    delta: number,
    startTimestamp: number,
    endTimestamp: number,
    isBottomPhrase: boolean,
  ) {
    console.debug('[ata] startAdaptiveSegment', {
      base,
      delta,
      startTimestamp,
      isBottomPhrase,
      freePlay: this.isFreePlay,
      goalTempo: this.goalTempo,
    });
    // Upper limit only applies to speed-ups. At base === upperLimit (e.g. 300 BPM), slow-down
    // must still run; otherwise ATA UI fires but audio/tempo never change.
    if (
      (delta > 0 && !this.isFreePlay && base >= this.goalTempo) ||
      (delta > 0 && base >= this.upperLimitTempo) ||
      (delta < 0 && base <= this.lowerLimitTempo)
    )
      return;
    const targetTempo = base + delta;
    const newTargetTempo = Math.max(
      delta > 0 ? Math.min(targetTempo, this.upperLimitTempo, this.goalTempo) : targetTempo,
      this.lowerLimitTempo,
    );
    const actualDelta = newTargetTempo - base;
    console.debug('[ata] startAdaptiveSegment', {
      base,
      actualDelta,
      startTimestamp,
      endTimestamp,
      isBottomPhrase,
    });
    this.base = base;
    this.delta = actualDelta;
    this.startTimestamp = startTimestamp;
    this.endTimestamp = endTimestamp;
    this.isActive = true;
    NotifyReactEvents.SetAdaptiveTempoState({
      state: 'started',
      value: this.base,
      isBottomPhrase,
    });
  }

  finishAdaptiveSegment() {
    if (!this.isActive) return;
    console.debug('[ata] finishAdaptiveSegment', this.base + this.delta);
    this.isActive = false;
    this.base = this.base + this.delta;
    NotifyReactEvents.SetAdaptiveTempoState({ state: 'completed', value: this.base });
    this.onDone.call(this.callbackScope, this.base);
  }

  abortAdaptiveSegment({ tempoRestorePoint: _ }: { tempoRestorePoint?: number }) {
    this.isActive = false;
    this.onDone.call(this.callbackScope, this.base);
  }

  onTick(currentTimestamp: number) {
    if (!this.isEnabled) return;
    console.debug(
      '[ata] onTick',
      currentTimestamp,
      this.isActive,
      this.startTimestamp,
      this.endTimestamp,
    );
    if (!this.isActive) return;
    const progress =
      (currentTimestamp - this.startTimestamp) / (this.endTimestamp - this.startTimestamp);
    const newTempo = this.base + this.delta * progress;
    if (progress >= 1) {
      this.finishAdaptiveSegment();
    }
    NotifyReactEvents.SetAdaptiveTempoState({ state: 'progress', value: newTempo });
    return newTempo;
    //NotifyReact(UpdateTempo(newTempo));
  }
}
